/**
 * Local player preferences.
 *
 * Purely client-side and deliberately so: nothing here reaches the server, for
 * the same reason the camera does not. How fast someone's mouse turns their
 * turret is not a fact about the match, and a sensitivity that travelled with an
 * account would be one more thing to get wrong at join time.
 *
 * `localStorage` rather than a cookie or the account: it survives a reload,
 * costs no request, and a player on a shared machine keeps their own feel.
 */
import { ref, watch } from 'vue';

const STORAGE_KEY = 'sow.settings.v1';

/**
 * Multiplier on the horizontal mouse sensitivity — turret traverse and
 * free-look, which are the same gesture and so move together.
 *
 * 1 is the tuned default. The bounds are wide enough to cover a slow trackpad
 * and a high-DPI gaming mouse without letting a stored value make the game
 * unplayable: at the bottom the gun still turns, and at the top it is fast
 * rather than uncontrollable.
 */
export const MIN_AIM_SENSITIVITY = 0.25;
export const MAX_AIM_SENSITIVITY = 3;
export const DEFAULT_AIM_SENSITIVITY = 1;

/** Rounds to the step the slider uses, so the stored value matches the UI. */
export function clampAimSensitivity(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_AIM_SENSITIVITY;
  const clamped = Math.min(MAX_AIM_SENSITIVITY, Math.max(MIN_AIM_SENSITIVITY, value));
  return Math.round(clamped * 20) / 20;
}

/**
 * How much the arena spends on looking good.
 *
 * A real setting rather than an auto-detect, because the thing that makes this
 * expensive — fill rate — depends on the display's resolution and the GPU in a
 * way no startup probe measures honestly, and because a player who wants frames
 * more than grass should simply be able to say so.
 */
export type SkyQuality = 'procedural' | 'gradient' | 'off';
export type GroundQuality = 'detailed' | 'plain' | 'grid';
/**
 * What stands beyond the arena bound. See `game/horizon.ts`.
 *
 * `scenic` is two layered ridge lines plus a belt of the map's own tall props;
 * `ring` is the single plain ridge line the A/B evaluation shipped on Ironback
 * Ridge, kept as the cheap tier; `off` is the bare fogged horizon the game had
 * before either. Its own dial rather than a rider on `ground`, for the reason
 * `effects` is: the ground is fill rate on every pixel of the floor, and the
 * background is a couple of draw calls at the edge of the fog — a player may
 * reasonably want either without the other.
 */
export type BackgroundQuality = 'scenic' | 'ring' | 'off';
/**
 * How much fire and smoke the arena draws. See `game/vfx.ts` for the table of
 * what each tier removes and why that thing and not another.
 *
 * Its own dial rather than a rider on `ground`, because it is a different cost
 * with a different shape: the ground is fill rate that is on screen every frame
 * of the match, and this is fill rate that arrives in bursts plus per-frame CPU
 * walking particle arrays. Someone on an integrated GPU may want the grass and
 * not the smoke, and there is no honest way to guess which.
 */
export type EffectsQuality = 'full' | 'reduced' | 'off';

export const SKY_QUALITIES: SkyQuality[] = ['procedural', 'gradient', 'off'];
export const GROUND_QUALITIES: GroundQuality[] = ['detailed', 'plain', 'grid'];
export const BACKGROUND_QUALITIES: BackgroundQuality[] = ['scenic', 'ring', 'off'];
export const EFFECTS_QUALITIES: EffectsQuality[] = ['full', 'reduced', 'off'];

/**
 * How loud the game is, and whether it makes any noise at all.
 *
 * Volume and mute are two settings rather than one, because they answer two
 * different questions: mute is "not right now" and must not cost the player the
 * level they had chosen. Collapsing them — muting by writing 0 to the volume —
 * means unmuting has nothing to go back to.
 *
 * **Zero is a legal volume**, which is the one way this clamp differs from the
 * aim sensitivity's: a gun that cannot turn is a broken game, and silence is a
 * preference.
 */
export const MIN_VOLUME = 0;
export const MAX_VOLUME = 1;
export const DEFAULT_VOLUME = 0.7;

/** Rounds to the step the slider uses, so the stored value matches the UI. */
export function clampVolume(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_VOLUME;
  const clamped = Math.min(MAX_VOLUME, Math.max(MIN_VOLUME, value));
  return Math.round(clamped * 20) / 20;
}

/**
 * How loud the music bed is, and whether there is one at all.
 *
 * **A second pair rather than a rider on the master pair**, and that is the
 * whole point of it: the two answer different questions. `muted` is *the game
 * makes no noise*; `musicMuted` is *keep the guns, lose the music* — which is
 * what somebody working, or listening to something else, or simply tired of a
 * loop actually wants. Collapsing them would make silencing the music cost the
 * player their gunfire, and a mute that takes the feedback with it is one nobody
 * uses.
 *
 * The default is **on**: a bed nobody hears is a feature that ships broken, and
 * the mute is one key away (see `MUSIC_MUTE_CODE` in `game/audio/music.ts`).
 *
 * Zero is a legal volume here for the same reason it is for the master.
 */
export const MIN_MUSIC_VOLUME = 0;
export const MAX_MUSIC_VOLUME = 1;
export const DEFAULT_MUSIC_VOLUME = 0.55;

/** Rounds to the step a slider would use, so a stored value matches the UI. */
export function clampMusicVolume(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_MUSIC_VOLUME;
  const clamped = Math.min(MAX_MUSIC_VOLUME, Math.max(MIN_MUSIC_VOLUME, value));
  return Math.round(clamped * 20) / 20;
}

interface StoredSettings {
  aimSensitivity: number;
  hangarRoom: boolean;
  sky: SkyQuality;
  ground: GroundQuality;
  background: BackgroundQuality;
  effects: EffectsQuality;
  previews: boolean;
  volume: number;
  muted: boolean;
  musicVolume: number;
  musicMuted: boolean;
}

const DEFAULTS: StoredSettings = {
  aimSensitivity: DEFAULT_AIM_SENSITIVITY,
  hangarRoom: true,
  sky: 'procedural',
  ground: 'detailed',
  background: 'scenic',
  effects: 'full',
  previews: true,
  volume: DEFAULT_VOLUME,
  muted: false,
  musicVolume: DEFAULT_MUSIC_VOLUME,
  musicMuted: false,
};

/** Keeps a stored enum inside its own set, so a hand-edited value cannot brick a scene. */
function oneOf<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return allowed.includes(value as T) ? (value as T) : fallback;
}

function load(): StoredSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULTS };
    const parsed = JSON.parse(raw) as Partial<StoredSettings>;
    return {
      aimSensitivity: clampAimSensitivity(Number(parsed.aimSensitivity)),
      hangarRoom:
        typeof parsed.hangarRoom === 'boolean' ? parsed.hangarRoom : DEFAULTS.hangarRoom,
      sky: oneOf(parsed.sky, SKY_QUALITIES, DEFAULTS.sky),
      ground: oneOf(parsed.ground, GROUND_QUALITIES, DEFAULTS.ground),
      background: oneOf(parsed.background, BACKGROUND_QUALITIES, DEFAULTS.background),
      effects: oneOf(parsed.effects, EFFECTS_QUALITIES, DEFAULTS.effects),
      previews: typeof parsed.previews === 'boolean' ? parsed.previews : DEFAULTS.previews,
      volume: clampVolume(Number(parsed.volume)),
      muted: typeof parsed.muted === 'boolean' ? parsed.muted : DEFAULTS.muted,
      musicVolume: clampMusicVolume(Number(parsed.musicVolume)),
      musicMuted:
        typeof parsed.musicMuted === 'boolean' ? parsed.musicMuted : DEFAULTS.musicMuted,
    };
  } catch {
    // Corrupt or unavailable storage must not stop the game starting; a private
    // window with storage disabled throws on read.
    return { ...DEFAULTS };
  }
}

const initial = load();

/** Horizontal sensitivity multiplier, 0.25–3. Reactive; persists on change. */
export const aimSensitivity = ref(initial.aimSensitivity);
/** How the sky is drawn. `gradient` and `off` trade looks for fill rate. */
export const skyQuality = ref<SkyQuality>(initial.sky);
/** How the ground is drawn. */
export const groundQuality = ref<GroundQuality>(initial.ground);
/** What stands beyond the arena bound. */
export const backgroundQuality = ref<BackgroundQuality>(initial.background);
/** How much fire and smoke the arena draws. */
export const effectsQuality = ref<EffectsQuality>(initial.effects);
/** Whether the garage renders model previews on its cards. */
export const previewsEnabled = ref(initial.previews);
/**
 * Whether the 3D hangar renders behind the garage.
 *
 * **Its own dial rather than a rider on `previews`**, because the two are
 * different costs answering different questions. `previews` is the little
 * turntables on shop cards — a mesh each, on demand. This is the *room*: a live
 * WebGL context running a render loop for as long as the garage is open,
 * whichever tab is in front of it. Somebody reading the shop wants the cards to
 * keep their models; somebody who has left the garage sitting open behind
 * another window wants the GPU back.
 *
 * The case it was added for is the admin panel: an operator who opens
 * `/admin` from the settings window is not looking at the game, and there is no
 * reason for a tank to be turning in a canvas they cannot see. Turning it off
 * falls back to the same still plate `HangarView` already uses when WebGL is
 * refused — a complete fallback that names the vehicle, not a blank area.
 */
export const hangarRoomEnabled = ref(initial.hangarRoom);
/** Master volume, 0–1. Reactive; persists on change. */
export const masterVolume = ref(initial.volume);
/** Silences everything without losing the chosen volume. */
export const audioMuted = ref(initial.muted);
/** How loud the music bed is under the cues, 0–1. */
export const musicVolume = ref(initial.musicVolume);
/**
 * Silences the music bed and nothing else.
 *
 * Toggled by `M` from anywhere in the app — see `installMusicMuteKey` in
 * `game/audio/music.ts` — and it persists, because "not right now" is almost
 * always "not this session either".
 */
export const musicMuted = ref(initial.musicMuted);

function persist(): void {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        aimSensitivity: aimSensitivity.value,
        hangarRoom: hangarRoomEnabled.value,
        sky: skyQuality.value,
        ground: groundQuality.value,
        background: backgroundQuality.value,
        effects: effectsQuality.value,
        previews: previewsEnabled.value,
        volume: masterVolume.value,
        muted: audioMuted.value,
        musicVolume: musicVolume.value,
        musicMuted: musicMuted.value,
      } satisfies StoredSettings),
    );
  } catch {
    // Not being able to remember the preference is not worth an error.
  }
}

watch(aimSensitivity, (value) => {
  const clamped = clampAimSensitivity(value);
  // Assigning inside the watcher would recurse; only correct an out-of-range
  // value, which re-triggers once and then settles.
  if (clamped !== value) {
    aimSensitivity.value = clamped;
    return;
  }
  persist();
});

watch(masterVolume, (value) => {
  // Same shape as the sensitivity watcher above, and for the same reason:
  // assigning inside the watcher recurses, so only an out-of-range value is
  // corrected, which re-triggers once and then settles.
  const clamped = clampVolume(value);
  if (clamped !== value) {
    masterVolume.value = clamped;
    return;
  }
  persist();
});

watch(musicVolume, (value) => {
  // Same shape as the two watchers above, and for the same reason.
  const clamped = clampMusicVolume(value);
  if (clamped !== value) {
    musicVolume.value = clamped;
    return;
  }
  persist();
});

watch(
  [
    skyQuality,
    groundQuality,
    backgroundQuality,
    effectsQuality,
    previewsEnabled,
    hangarRoomEnabled,
    audioMuted,
    musicMuted,
  ],
  persist,
);

/**
 * Whether every setting is still at its shipped value.
 *
 * Here rather than in `SettingsPanel.vue` because `DEFAULTS` is here: a window
 * that kept its own list of defaults would be a second statement of the same
 * facts, and the one that goes stale is always the copy — a setting added below
 * would silently stop being covered, and *Reset to default* would leave one
 * value behind with no sign that it had.
 */
export function isDefaultSettings(): boolean {
  return (
    aimSensitivity.value === DEFAULTS.aimSensitivity &&
    skyQuality.value === DEFAULTS.sky &&
    groundQuality.value === DEFAULTS.ground &&
    backgroundQuality.value === DEFAULTS.background &&
    effectsQuality.value === DEFAULTS.effects &&
    previewsEnabled.value === DEFAULTS.previews &&
    hangarRoomEnabled.value === DEFAULTS.hangarRoom &&
    masterVolume.value === DEFAULTS.volume &&
    audioMuted.value === DEFAULTS.muted &&
    musicVolume.value === DEFAULTS.musicVolume &&
    musicMuted.value === DEFAULTS.musicMuted
  );
}

/**
 * Everything back to shipped values.
 *
 * One write per ref rather than a bulk replace, because each has its own watcher
 * doing the clamping and the persisting — see the three above. The last one to
 * fire is what lands in storage, and every one of them writes the whole object,
 * so a single reset is a handful of `setItem` calls and one final state. That is
 * cheaper than it looks and much harder to get wrong than a second persist path.
 */
export function resetSettings(): void {
  aimSensitivity.value = DEFAULTS.aimSensitivity;
  skyQuality.value = DEFAULTS.sky;
  groundQuality.value = DEFAULTS.ground;
  backgroundQuality.value = DEFAULTS.background;
  effectsQuality.value = DEFAULTS.effects;
  previewsEnabled.value = DEFAULTS.previews;
  hangarRoomEnabled.value = DEFAULTS.hangarRoom;
  masterVolume.value = DEFAULTS.volume;
  audioMuted.value = DEFAULTS.muted;
  musicVolume.value = DEFAULTS.musicVolume;
  musicMuted.value = DEFAULTS.musicMuted;
}

/** Read by the scene, which is not a Vue component and holds no subscription. */
export function currentSkyQuality(): SkyQuality {
  return skyQuality.value;
}

export function currentGroundQuality(): GroundQuality {
  return groundQuality.value;
}

export function currentBackgroundQuality(): BackgroundQuality {
  return backgroundQuality.value;
}

export function currentEffectsQuality(): EffectsQuality {
  return effectsQuality.value;
}

export function previewsAllowed(): boolean {
  return previewsEnabled.value;
}

/**
 * Reads the current multiplier.
 *
 * A function rather than the ref itself so the render loop — which is not a Vue
 * component and has no reactivity — can sample it per frame and pick up a change
 * the moment the slider moves, without a subscription to tear down.
 */
export function currentAimSensitivity(): number {
  return aimSensitivity.value;
}

/**
 * The gain the audio bus should actually carry: the chosen volume, or zero
 * while muted.
 *
 * Resolved here rather than in the audio module so there is one answer to "how
 * loud is the game", and so muting cannot be implemented twice and disagree.
 */
export function currentMasterVolume(): number {
  return audioMuted.value ? 0 : clampVolume(masterVolume.value);
}
